import { useState } from 'react';
import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { CommandBlock } from '../../../components/CommandBlock';
import { ContentSection } from '../../../components/ContentSection';
import { cardSurfaceSx, tokens } from '../../../theme';
import { FormSwitch } from './FormSwitch';
import {
  COMMAND_FORMS,
  DOWNLOADS_DIR,
  MARKETPLACE_DIR,
  MARKETPLACE_NAME,
  ORG_MARKETPLACE_NAME,
  pluginFileName,
} from './constants';
import type { CommandForm } from './constants';

type Route = 'toolkit' | 'claudeai';
type OrgForm = CommandForm | 'desktop';

/** Claude Desktop installs from the organisation's marketplace only, so it is offered on that route alone. */
const ORG_FORMS = [
  ...COMMAND_FORMS,
  { value: 'desktop', label: 'Claude Desktop' },
] as const satisfies readonly { value: OrgForm; label: string }[];

const Meta = ({ children }: { children: ReactNode }) => (
  <Typography variant="body2" sx={{ color: 'text.meta' }}>
    {children}
  </Typography>
);

/** Keypresses in a menu rather than something to paste, so a list rather than a command block. */
const Keypresses = ({ children }: { children: ReactNode }) => (
  <Box component="ol" sx={{ margin: 0, paddingLeft: 5, typography: 'body2', '& li + li': { mt: 1 } }}>
    {children}
  </Box>
);

const RELOAD_NOTE = (
  <Meta>
    If Claude Code is already running, run <code>/reload-plugins</code> or restart it to load the
    plugin.
  </Meta>
);

/** The `<ol>` carries the numbering for a reader; the drawn chip is decoration. */
const Step = ({ number, title, children }: { number: number; title: string; children: ReactNode }) => (
  <Stack component="li" direction="row" spacing={4}>
    <Box
      aria-hidden
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        backgroundColor: 'surface.sunken',
        color: 'text.secondary',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {number}
    </Box>
    <Stack spacing={3} sx={{ minWidth: 0, flexGrow: 1 }}>
      <Typography variant="h4" component="h3" sx={{ lineHeight: '28px' }}>
        {title}
      </Typography>
      {children}
    </Stack>
  </Stack>
);

/**
 * One install route to pick. The whole card selects it for a pointer; the
 * title is the real button, so the keyboard and a screen reader get one
 * control rather than a clickable box. The selected card carries the form
 * switch and the red ring `SearchField` uses for focus.
 */
const RouteCard = ({
  title,
  description,
  selected,
  onSelect,
  children,
}: {
  title: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
  children: ReactNode;
}) => (
  <Box
    onClick={onSelect}
    sx={(theme) => ({
      ...cardSurfaceSx,
      padding: 4,
      cursor: selected ? 'default' : 'pointer',
      borderColor: selected ? 'primary.main' : undefined,
      boxShadow: selected ? `0 0 0 3px ${theme.palette.accent.brandSurface}` : undefined,
    })}
  >
    <Box
      component="button"
      type="button"
      aria-pressed={selected}
      sx={{
        all: 'unset',
        cursor: 'inherit',
        typography: 'h4',
        borderRadius: `${String(tokens.radius.md)}px`,
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
      }}
    >
      {title}
    </Box>
    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
      {description}
    </Typography>
    {selected && <Box sx={{ mt: 3 }}>{children}</Box>}
  </Box>
);

const ScopeBox = () => (
  <Box sx={{ ...cardSurfaceSx, backgroundColor: 'surface.raised', padding: 4 }}>
    <Typography variant="overline" component="p" sx={{ color: 'text.secondary', letterSpacing: '1px' }}>
      Scope
    </Typography>
    <Typography variant="body2" sx={{ mt: 1 }}>
      Install at <code>user</code>. One install covers every project, and{' '}
      <code>luna-telemetry</code> costs you nothing in a directory you never bind — its hooks do
      nothing at all without a bind file. In Claude Code&rsquo;s <code>/plugin install</code> flow,
      pick <strong>User</strong> when it asks.
    </Typography>

    <Box component="table" sx={{ mt: 3, fontSize: 13, borderCollapse: 'collapse', width: '100%' }}>
      <Box component="thead">
        <Box component="tr">
          <Box component="th" sx={{ textAlign: 'left', color: 'text.secondary', paddingBlock: 1 }}>
            Scope
          </Box>
          <Box component="th" sx={{ textAlign: 'left', color: 'text.secondary', paddingBlock: 1 }}>
            Who gets it
          </Box>
        </Box>
      </Box>
      <Box component="tbody">
        <Box component="tr">
          <Box component="td" sx={{ paddingBlock: 1 }}>
            <code>user</code>
          </Box>
          <Box component="td" sx={{ paddingBlock: 1 }}>
            you, in every repository you open — recommended
          </Box>
        </Box>
        <Box component="tr">
          <Box component="td" sx={{ paddingBlock: 1 }}>
            <code>project</code>
          </Box>
          <Box component="td" sx={{ paddingBlock: 1 }}>
            everyone who clones the repo — it is committed
          </Box>
        </Box>
        <Box component="tr">
          <Box component="td" sx={{ paddingBlock: 1 }}>
            <code>local</code>
          </Box>
          <Box component="td" sx={{ paddingBlock: 1 }}>
            you, in this directory only
          </Box>
        </Box>
      </Box>
    </Box>

    <Typography variant="body2" sx={{ mt: 3, color: 'text.meta' }}>
      <code>local</code> records the plugin in that directory&rsquo;s{' '}
      <code>.claude/settings.local.json</code>, creating the file if it is not there. Check the
      repository&rsquo;s <code>.gitignore</code> covers it before you commit.
    </Typography>
    <Typography variant="body2" sx={{ mt: 3, color: 'text.meta' }}>
      On the Augment Toolkit route, <code>project</code> does not survive. It commits an entry
      naming the marketplace, but that marketplace registers as an absolute path on your machine,
      so a teammate who clones the repo gets a reference they cannot resolve.
    </Typography>
  </Box>
);

const ToolkitSteps = ({
  form,
  version,
  releaseCard,
}: {
  form: CommandForm;
  version: string;
  releaseCard: ReactNode;
}) => (
  <>
    <Step number={1} title="Get the marketplace">
      {releaseCard}
    </Step>

    <Step number={2} title="Add the marketplace">
      <Typography variant="body2">Extract the zip into your Claude Code marketplaces directory:</Typography>
      <CommandBlock>
        {`mkdir -p ${MARKETPLACE_DIR}\nunzip ${DOWNLOADS_DIR}/${pluginFileName(version)} -d ${MARKETPLACE_DIR}/`}
      </CommandBlock>
      <Meta>
        The folder has to stay where it lands. Claude Code stores the path and reads the marketplace
        from it on every start, so moving or deleting it later breaks every plugin installed from it.
      </Meta>
      <Typography variant="body2">Then register it:</Typography>
      <CommandBlock>
        {form === 'shell'
          ? `claude plugin marketplace add ${MARKETPLACE_DIR}/${MARKETPLACE_NAME}`
          : `claude\n/plugin marketplace add ${MARKETPLACE_DIR}/${MARKETPLACE_NAME}`}
      </CommandBlock>
      <Meta>Adding the marketplace is one-off per machine, not per repository.</Meta>
    </Step>

    <Step number={3} title="Install the plugins">
      <Typography variant="body2">
        Install both. <code>and-core</code> goes first — it carries <code>/and-update</code>, which
        keeps every AND plugin current from here on, so the unzip is the only one you do by hand.
      </Typography>
      <CommandBlock>
        {form === 'shell'
          ? `claude plugin install and-core@${MARKETPLACE_NAME}\nclaude plugin install luna-telemetry@${MARKETPLACE_NAME}`
          : `/plugin install and-core@${MARKETPLACE_NAME}\n/plugin install luna-telemetry@${MARKETPLACE_NAME}`}
      </CommandBlock>
      {RELOAD_NOTE}
      <ScopeBox />
    </Step>

    <Step number={4} title="Check it is installed">
      <CommandBlock>{form === 'shell' ? 'claude plugin list' : '/plugin'}</CommandBlock>
      <Typography variant="body2">Both plugins should be listed, each in this shape:</Typography>
      <CommandBlock>
        {`and-core@${MARKETPLACE_NAME}\n  Version: <plugin version>\n  Scope: user\n  Status: enabled\n\nluna-telemetry@${MARKETPLACE_NAME}\n  Version: <plugin version>\n  Scope: user\n  Status: enabled`}
      </CommandBlock>
      <Meta>
        Each plugin carries a version of its own, and it is not the {version} on this page — that
        one numbers the release as a whole. A plugin&rsquo;s number moves only when that plugin
        changes, so a release that leaves one untouched is not a failed install.
      </Meta>
    </Step>

    <Step number={5} title="Updating">
      <Typography variant="body2">
        Run <code>/and-update</code> in Claude Code. It checks the published release, signs you in
        over SSO, verifies the download against its published checksum, swaps the folder in place,
        and refreshes the marketplace and every plugin you installed from it.
      </Typography>
      <CommandBlock>/and-update</CommandBlock>
      <Typography variant="body2">
        You do not have to watch this page for it. <code>and-core</code> checks at the start of
        every session and tells you when a newer release is out.
      </Typography>
      <Meta>
        Restart Claude Code for the update to take effect — the swapped files are on disk, but the
        running session still has the old plugin loaded.
      </Meta>
    </Step>
  </>
);

const ClaudeAiSteps = ({ form }: { form: OrgForm }) => (
  <>
    <Step number={1} title="Get the marketplace">
      <Box sx={{ ...cardSurfaceSx, padding: 6 }}>
        <Typography variant="h4" component="p">
          Hosted on claude.ai
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.meta', mt: 1 }}>
          Published to AND&rsquo;s Claude organisation as <code>{ORG_MARKETPLACE_NAME}</code>, so
          there is nothing to download.
        </Typography>
      </Box>
    </Step>

    <Step number={2} title="Add the marketplace">
      {form === 'desktop' ? (
        <Typography variant="body2">
          Nothing to add. Claude Desktop already has the organisation&rsquo;s marketplace.
        </Typography>
      ) : (
        <>
          <Typography variant="body2">
            Adding it only works from inside Claude Code — there is no shell command for it. Start
            Claude Code:
          </Typography>
          <CommandBlock>claude</CommandBlock>
          <Keypresses>
            <li>
              Run <code>/plugin</code> and go to <strong>Marketplaces</strong>.
            </li>
            <li>
              Find <code>{ORG_MARKETPLACE_NAME}</code> and select it.
            </li>
            <li>
              Press <strong>Enter</strong> to add it, then <strong>y</strong> to confirm.
            </li>
            <li>
              Press <strong>Esc</strong> three times to leave the menu.
            </li>
          </Keypresses>
          <Meta>Adding the marketplace is one-off per machine, not per repository.</Meta>
        </>
      )}
    </Step>

    <Step number={3} title="Install the plugin">
      {form === 'desktop' ? (
        <Keypresses>
          <li>
            Open Claude Desktop and click the <strong>Code</strong> tab.
          </li>
          <li>
            Type this in the prompt box:
            <Box sx={{ mt: 2 }}>
              <CommandBlock>{`/plugin install luna-telemetry@${ORG_MARKETPLACE_NAME}`}</CommandBlock>
            </Box>
          </li>
          <li>
            Click <strong>Add</strong>.
          </li>
        </Keypresses>
      ) : (
        <CommandBlock>
          {form === 'shell'
            ? `claude plugin install luna-telemetry@${ORG_MARKETPLACE_NAME}`
            : `/plugin install luna-telemetry@${ORG_MARKETPLACE_NAME}`}
        </CommandBlock>
      )}
      <Meta>
        Only <code>luna-telemetry</code>. <code>and-core</code> is for zip installs: its{' '}
        <code>/and-update</code> would add a second, zip copy of every plugin.
      </Meta>
      {form !== 'desktop' && RELOAD_NOTE}
      <ScopeBox />
    </Step>

    <Step number={4} title="Check it is installed">
      {form === 'desktop' ? (
        <Typography variant="body2">
          Open <strong>Plugins</strong>, then <strong>Yours</strong>. Luna Telemetry should be listed
          under <strong>From your organization</strong>.
        </Typography>
      ) : (
        <>
          <CommandBlock>{form === 'shell' ? 'claude plugin list' : '/plugin'}</CommandBlock>
          <Typography variant="body2">It should be listed in this shape:</Typography>
          <CommandBlock>
            {`luna-telemetry@${ORG_MARKETPLACE_NAME}\n  Version: 0001\n  Scope: user\n  Status: ✔ enabled`}
          </CommandBlock>
          <Meta>
            claude.ai numbers the versions it syncs itself — <code>0001</code>, <code>0002</code> and
            on — so they will not match the release version on the Augment Toolkit route.
          </Meta>
        </>
      )}
    </Step>

    <Step number={5} title="Updating">
      <Typography variant="body2">
        Nothing to run. Claude keeps plugins from the claude.ai marketplace up to date itself, on its
        own schedule.
      </Typography>
    </Step>
  </>
);

type InstallRoutesProps = {
  /** The release this page fetched, which names the zip the reader downloads. */
  version: string;
  /** The version card and download button, owned by the page because it owns the fetch. */
  releaseCard: ReactNode;
};

/**
 * The two ways to get the plugins: the zip this page serves, and the claude.ai
 * organisation marketplace GitLab syncs from the same repository. The reader
 * picks one and sees only its steps, at every width. Each route keeps its own
 * form, because the two offer different ones — Claude Desktop only exists on
 * the claude.ai route.
 */
export const InstallRoutes = ({ version, releaseCard }: InstallRoutesProps) => {
  const [route, setRoute] = useState<Route>('toolkit');
  const [toolkitForm, setToolkitForm] = useState<CommandForm>('shell');
  const [orgForm, setOrgForm] = useState<OrgForm>('shell');

  return (
    <ContentSection
      heading="Marketplace installation"
      caption="There are two ways to get the plugin — choose the installation you prefer."
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 4,
          mb: 8,
        }}
      >
        <RouteCard
          title="Augment Toolkit"
          description="Download the marketplace from this page and install it from a folder on your machine. Claude Code only."
          selected={route === 'toolkit'}
          onSelect={() => {
            setRoute('toolkit');
          }}
        >
          <FormSwitch
            label="Augment Toolkit command form"
            options={COMMAND_FORMS}
            value={toolkitForm}
            onChange={setToolkitForm}
          />
        </RouteCard>
        <RouteCard
          title="Claude.ai Marketplace"
          description="Install from AND’s organisation marketplace on claude.ai. Works in Claude Code and Claude Desktop."
          selected={route === 'claudeai'}
          onSelect={() => {
            setRoute('claudeai');
          }}
        >
          <FormSwitch
            label="Claude.ai Marketplace command form"
            options={ORG_FORMS}
            value={orgForm}
            onChange={setOrgForm}
          />
        </RouteCard>
      </Box>

      <Stack component="ol" spacing={6} sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {route === 'toolkit' ? (
          <ToolkitSteps form={toolkitForm} version={version} releaseCard={releaseCard} />
        ) : (
          <ClaudeAiSteps form={orgForm} />
        )}
      </Stack>
    </ContentSection>
  );
};
